// src/ml/garmentCoverage.js

// Normalised mannequin landmarks (0 = FEET, 1 = HEAD)
// These are mannequin-space "body truth" anchors.
export const BODY = {
  FEET: 0.0,
  KNEE: 0.26,
  MID_THIGH: 0.36,
  HIP: 0.52,
  WAIST: 0.60,
  CHEST: 0.78,
  SHOULDER: 0.88,
  HEAD: 1.0,
};

// Coverage contracts per category
// anchor = where the garment TOP should land on the mannequin
export const GARMENT_COVERAGE = {
top: {
  // put the *top edge* closer to shoulder line
  anchor: BODY.SHOULDER,   // keep anchor semantic
  topLift: 0.275,           // was 0.04  (raises garment)

  // tops should NOT cover 40–50% of body height (that’s dress territory)
  // use a tighter contract so scaling doesn’t blow it up
  minCoverage: 0.32,       // ~cropped tee / short top
  maxCoverage: 0.44,       // ~standard top length

  // tops should not be allowed to go super wide
  widthMin: 0.3,
  widthMax: 0.5,
},

bottom: {
  anchor: BODY.HIP,
  topLift: 0.22,      // was 0.18 (a touch higher to stop “low-slung” look)
  minCoverage: 0.46,  // was 0.5  (allow less vertical fill)
  maxCoverage: 0.56,  // was 0.6  (cap vertical stretch)
  widthMin: 0.75,     // was 0.95 (HARD clamp smaller)
  widthMax: 0.9,     // was 1.0  (HARD clamp smaller)
},
"bottom:shorts_mini": {
  anchor: BODY.HIP,
  topLift: 0.0,
  minCoverage: 0.16,
  maxCoverage: 0.23,
  widthMin: 0.7,
  widthMax: 0.9,
},

"bottom:shorts": {
  anchor: BODY.HIP,
  topLift: 0.05,
  minCoverage: 0.24,
  maxCoverage: 0.32,
  widthMin: 0.7,
  widthMax: 0.9,
},

"bottom:bermuda": {
  anchor: BODY.HIP,
  topLift: 0.1,
  minCoverage: 0.32,
  maxCoverage: 0.40,
  widthMin: 0.7,
  widthMax: 0.9,
},

dress: {
  anchor: BODY.SHOULDER,
  minCoverage: 0.58,
  maxCoverage: 0.72,
  widthMin: 0.90,
  widthMax: 1.20,
},

"dress:mini": {
  anchor: BODY.SHOULDER,
  topLift: -0.09,
  minCoverage: 0.32,
  maxCoverage: 0.42,
  widthMin: 0,
  widthMax: 0.1,
},

"dress:midi": {
  anchor: BODY.SHOULDER,
  topLift: -0.09,
  minCoverage: 0.4,
  maxCoverage: 0.55,
  widthMin: 0.75,
  widthMax: 1,
},

"dress:maxi": {
  anchor: BODY.SHOULDER,
  topLift: -0.18,
  minCoverage: 0.90,
  maxCoverage: 0.95,
  widthMin: 0.90,
  widthMax: 1.15,
},

outer: {
  anchor: BODY.SHOULDER,

  // Outerwear should sit ON the shoulder line, not get yanked up.
  topLift: 0.2,           // was 0.30 (too aggressive)
  // Jackets typically cover past waist; cropped ones still > tees.
  minCoverage: 0.35,       // was 0.32
  maxCoverage: 0.62,       // was 0.46
  // Allow it to actually be roomy.
  widthMin: 0.9,          // was 0.95
  widthMax: 1.1,          // was 1.20
},
};

// Optional minimum world height floors per category (prevents tiny garments)
export const MIN_HEIGHT_FRAC = {
  top: 0.22,
  bottom: 0.40,
  dress: 0.45,
  outer: 0.35,
};

/**
 * Heuristic: skirts often look "small" because bbox coverage is short + wide.
 * We boost bottom garments that are wide (aspect high) but short coverage.
 */
export function skirtLikeBoost({ category, aspect, coverageNorm }) {
  if (category !== "bottom") return { heightMul: 1.1, widthMul: 1.1 };

  const isShort = coverageNorm < 0.42; // skirt/shorts territory
  const isWide = aspect > 0.85; // more width than a narrow jeans crop

  if (isShort && isWide) {
    return { heightMul: 1.35, widthMul: 1.18 };
  }

  return { heightMul: 1.0, widthMul: 1.0 };
}
